import React from 'react';
import { PayoutBreakdown } from '../../types/career';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { formatCurrencyUAH, formatCurrencyUSD, formatPercent } from '../../utils/formatters';
import { Receipt, Landmark, Wallet, ShieldAlert, ArrowDownRight } from 'lucide-react';

export interface TaxSummaryCardProps {
  payout: PayoutBreakdown;
  className?: string;
}

export const TaxSummaryCard: React.FC<TaxSummaryCardProps> = ({ payout, className = '' }) => {
  const effectiveTaxRate =
    payout.totalGrossIncomeUAH > 0
      ? payout.totalTaxesUAH / payout.totalGrossIncomeUAH
      : 0;

  const netRetentionRate =
    payout.totalGrossIncomeUAH > 0
      ? payout.estimatedNetIncomeUAH / payout.totalGrossIncomeUAH
      : 0;

  return (
    <Card variant="elevated" className={`border-slate-700/60 overflow-hidden ${className}`}>
      <CardHeader className="pb-3 border-b border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm sm:text-base flex items-center gap-2 min-w-0">
            <Landmark className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <span className="truncate sm:whitespace-normal">Оподаткування ФОП 3-тя група</span>
          </CardTitle>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 font-mono whitespace-nowrap shrink-0">
            Податки: {formatPercent(effectiveTaxRate, 1)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-4">
        {/* Tax breakdown rows */}
        <div className="space-y-2.5 text-xs">
          {/* 5% Single Tax */}
          <div className="flex items-center justify-between gap-2 py-1 border-b border-white/5">
            <div className="flex items-center gap-2 text-text-secondary min-w-0">
              <Receipt className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
              <span className="whitespace-nowrap">Єдиний податок (5%)</span>
            </div>
            <span className="font-mono font-medium text-text-primary whitespace-nowrap flex-shrink-0">
              {formatCurrencyUAH(payout.fopSingleTaxUAH, 2)}
            </span>
          </div>

          {/* 1% Military Levy */}
          <div className="flex items-center justify-between gap-2 py-1 border-b border-white/5">
            <div className="flex items-center gap-2 text-text-secondary min-w-0">
              <ShieldAlert className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
              <span className="whitespace-nowrap">Військовий збір (1%)</span>
            </div>
            <span className="font-mono font-medium text-text-primary whitespace-nowrap flex-shrink-0">
              {formatCurrencyUAH(payout.militaryLevyUAH, 2)}
            </span>
          </div>

          {/* Fixed USV */}
          <div className="flex items-center justify-between gap-2 py-1 border-b border-white/5">
            <div
              className="flex items-center gap-2 text-text-secondary min-w-0"
              title="Єдиний соціальний внесок (22% від мін. з/п)"
            >
              <Landmark className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
              <span className="whitespace-nowrap">ЄСВ (22%)</span>
            </div>
            <span className="font-mono font-medium text-text-primary whitespace-nowrap flex-shrink-0">
              {formatCurrencyUAH(payout.monthlyUsvUAH, 2)}
            </span>
          </div>

          {/* Total Taxes Row */}
          <div className="flex items-center justify-between gap-2 pt-1 font-semibold text-rose-300">
            <div className="flex items-center gap-1.5 min-w-0">
              <ArrowDownRight className="w-4 h-4 text-rose-400 flex-shrink-0" />
              <span className="whitespace-nowrap">Всього податків:</span>
            </div>
            <span className="font-mono whitespace-nowrap flex-shrink-0">
              -{formatCurrencyUAH(payout.totalTaxesUAH, 2)}
            </span>
          </div>
        </div>

        {/* Net Take-Home Hero Box */}
        <div className="bg-gradient-to-br from-emerald-950/50 to-slate-900/90 border border-emerald-500/30 rounded-xl p-3.5 sm:p-4 shadow-[0_0_20px_rgba(16,185,129,0.15)] space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-1.5 text-xs text-text-secondary">
            <span className="flex items-center gap-1.5 text-emerald-300 font-semibold whitespace-nowrap">
              <Wallet className="w-4 h-4 text-accent-neon flex-shrink-0" />
              <span>Чистий дохід («на руки»):</span>
            </span>
            <span className="text-[11px] font-mono text-text-muted whitespace-nowrap">
              Збереження: {formatPercent(netRetentionRate, 1)}
            </span>
          </div>

          <div className="flex items-baseline justify-between gap-2 flex-wrap pt-0.5">
            <div className="text-xl sm:text-2xl font-bold font-mono text-accent-neon tracking-tight whitespace-nowrap">
              {formatCurrencyUAH(payout.estimatedNetIncomeUAH, 2)}
            </div>
            <div className="text-xs sm:text-sm font-mono text-emerald-400/80 whitespace-nowrap">
              ≈ {formatCurrencyUSD(payout.estimatedNetIncomeUSD, 0)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
